import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { AnyDocument, Pagination, Sorting } from '../../../../types';
import { createSearchParams } from '../../../../utils/queries';
import { InceptHeader } from '../../../constants/InceptHeader';
import { useRequestConstructor } from '../../../hooks/useRequestConstructor';
import { createHeadersForValue } from '../../../logic/createHeadersForValue';
import { unwrapPaginatedResponseDataAs } from '../../../logic/unwrapPaginatedResponseDataAs';
import { ACTIVITIES_BASE_PATH } from '../../constants/activitiesPath';
import { ActivitySubjectType } from '../../constants/ActivitySubjectType';
import { getActivities } from '../../requests/getActivities';
import { ActivitiesSearchParams } from '../../types/ActivityParams';

export const useDocumentActivities = (
  documentId: AnyDocument['id'],
  documentDefinition: AnyDocument['definition'],
  pagination: Pagination,
  sorting: Sorting,
  searchParams?: ActivitiesSearchParams,
) => {
  const { constructGet } = useRequestConstructor();

  const mergedParams = useMemo(
    () => createSearchParams(searchParams, pagination, sorting),
    [searchParams, pagination, sorting],
  );

  const { data, refetch, ...rest } = useQuery<
    Awaited<ReturnType<typeof getActivities>>
  >({
    queryKey: [
      ACTIVITIES_BASE_PATH,
      ActivitySubjectType.Document,
      documentId,
      documentDefinition,
      searchParams,
      pagination,
      sorting,
    ],
    queryFn: async () =>
      getActivities(
        constructGet,
        ActivitySubjectType.Document,
        documentId,
        createHeadersForValue(InceptHeader.DefinitionName, documentDefinition),
        mergedParams,
      ),
    keepPreviousData: true,
  });

  return {
    ...unwrapPaginatedResponseDataAs(data, refetch, 'activities'),
    ...rest,
  };
};
